import { useCallback, useState } from 'react';
import { ApiError, useCallApi } from './common';
import {
  EventApi,
  EventData,
  EventEntryData,
  EventEntryCreateRequest,
  EventEntryCreateResponse,
  EventListResponse,
  EventEntryListResponse,
  TypingApi,
  TypingBestScore,
  TypingGetBestScoresResponse,
  ApiV1TypingBestScoresGetThemeTypesEnum,
} from '../../api';

const SUB_SITE = 'typing';

export const useEvent = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { callApi, defaultConfiguration } = useCallApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [activeEvents, setActiveEvents] = useState<EventData[] | undefined>(undefined);
  const [eventEntries, setEventEntries] = useState<EventEntryData[] | undefined>(undefined);
  const [bestScores, setBestScores] = useState<TypingBestScore[]>([]);

  const getActiveEvents = useCallback(
    async () => {
      setLoading(true);
      setError(null);
      const api = new EventApi(await defaultConfiguration());
      const response = await callApi<EventListResponse, null>(null, () => api.apiV1EventsGet(SUB_SITE));

      if (response.success) {
        setActiveEvents(response.success.data);
      } else {
        setError(response.error);
      }
      setLoading(false);
      return response;
    },
    [callApi, defaultConfiguration]
  );

  const getEventEntries = useCallback(
    async (eventId: number) => {
      setLoading(true);
      setError(null);
      const api = new EventApi(await defaultConfiguration());
      const response = await callApi<EventEntryListResponse, null>(null, () => api.apiV1EventsEventIdEntriesGet(eventId));

      if (response.success) {
        setEventEntries(response.success.data);
      } else {
        setError(response.error);
      }
      setLoading(false);
      return response;
    },
    [callApi, defaultConfiguration]
  );

  const createEventEntry = useCallback(
    async (eventId: number, request: EventEntryCreateRequest) => {
      setLoading(true);
      setError(null);
      const api = new EventApi(await defaultConfiguration());
      const response = await callApi<EventEntryCreateResponse, EventEntryCreateRequest>(request, (req) => api.apiV1EventsEventIdEntriesPost(eventId, req));

      if (!response.success) {
        setError(response.error);
      }
      setLoading(false);
      return response;
    },
    [callApi, defaultConfiguration]
  );

  const getBestScores = useCallback(
    async (themeTypes: readonly ApiV1TypingBestScoresGetThemeTypesEnum[], dateFrom?: string | null, dateTo?: string | null) => {
      setLoading(true);
      setError(null);
      const api = new TypingApi(await defaultConfiguration());
      const response = await callApi<TypingGetBestScoresResponse, null>(null, () =>
        api.apiV1TypingBestScoresGet([...themeTypes], dateFrom, dateTo)
      );

      if (response.success) {
        setBestScores(response.success.data);
      } else {
        setError(response.error);
      }
      setLoading(false);
      return response;
    },
    [callApi, defaultConfiguration]
  );

  return {
    error,
    loading,
    activeEvents,
    eventEntries,
    bestScores,
    getActiveEvents,
    getEventEntries,
    createEventEntry,
    getBestScores,
  };
};
